import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PreviewIcon from "@mui/icons-material/Preview";

import api from "../../services/api/axios";
import BrandSpinner from "../../shared/components/BrandSpinner";
import BackButton from "../../shared/components/BackButton";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from "../../theme/buttonstyles";

const PAGE_BG = "#f7f9fc";
const CARD_SX = { border: `1.5px solid ${BRAND_BLUE}`, borderRadius: 2, boxShadow: "0 8px 20px rgba(15,23,42,0.08)", background: "#fff", overflow: "hidden" };
const HEAD_CELL_SX = { backgroundColor: BRAND_BLUE, color: "#fff", fontSize: "0.72rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" };

const IMPORT_TYPES = [
  { value: "members", label: "Members", helper: "Member records and optional membership renewal fields." },
  { value: "contacts", label: "Contacts", helper: "Membership contacts and optional member links." },
  { value: "event_attendance", label: "Event Attendance", helper: "Attendance rows matched to existing events and attendees." },
  { value: "sponsorships", label: "Sponsorships", helper: "Sponsor commitments matched to members and events." },
];

function StatusChip({ row }) {
  if (row.errors?.length) return <Chip size="small" label="Error" sx={{ bgcolor: "#FCE4EC", color: "#AD1457", fontWeight: 800 }} />;
  if (row.warnings?.length) return <Chip size="small" label="Warning" sx={{ bgcolor: "#FFF8E1", color: "#E65100", fontWeight: 800 }} />;
  return <Chip size="small" label={row.action || "Ready"} sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 800, textTransform: "capitalize" }} />;
}

function display(value) {
  if (value == null || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export default function MembershipImports() {
  const inputRef = useRef(null);
  const [importType, setImportType] = useState("members");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [options, setOptions] = useState({
    merge_updates: true,
    create_missing_members: false,
    create_missing_contacts: false,
  });

  const selectedType = useMemo(() => IMPORT_TYPES.find((type) => type.value === importType), [importType]);
  const rows = preview?.rows || [];
  const canApply = rows.length > 0 && !rows.some((row) => row.errors?.length);

  const resetPreview = () => {
    setPreview(null);
    setResult(null);
    setError("");
  };

  const chooseFile = (event) => {
    setFile(event.target.files?.[0] || null);
    resetPreview();
  };

  const previewFile = async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    setResult(null);
    const form = new FormData();
    form.append("file", file, file.name);
    try {
      const { data } = await api.post(`/membership/imports/${importType}/preview`, form);
      setPreview(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Preview failed.");
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    setError("");
    try {
      const response = await api.get(`/membership/imports/${importType}/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `membership_${importType}_import_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Template download failed.");
    }
  };

  const applyImport = async () => {
    if (!canApply) return;
    setApplying(true);
    setError("");
    try {
      const { data } = await api.post(`/membership/imports/${importType}/apply`, {
        rows,
        options,
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Apply failed.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <Box sx={{ position: "sticky", top: 0, zIndex: 5, borderBottom: "1px solid #dce4ef", background: "#fff" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: { xs: 2, md: 3 }, py: 1.75, flexWrap: "wrap" }}>
          <BackButton to="/membership" />
          <Box sx={{ width: 32, height: 32, borderRadius: 2, background: BRAND_GREEN, color: "#fff", display: "grid", placeItems: "center" }}>
            <CloudUploadIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 21, fontWeight: 800, color: BRAND_BLUE }}>Membership Imports</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13 }}>Preview, validate, then apply spreadsheet imports.</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={4}>
            <Card sx={CARD_SX}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <TextField
                    select
                    label="Import Type"
                    value={importType}
                    onChange={(event) => {
                      setImportType(event.target.value);
                      setFile(null);
                      resetPreview();
                      if (inputRef.current) inputRef.current.value = "";
                    }}
                    fullWidth
                  >
                    {IMPORT_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </TextField>
                  <Typography sx={{ color: "#64748b", fontSize: 14 }}>{selectedType?.helper}</Typography>
                  <Button startIcon={<DownloadIcon />} onClick={downloadTemplate} sx={SECONDARY_BUTTON_SX}>
                    Download Template
                  </Button>
                  <Button component="label" startIcon={<CloudUploadIcon />} sx={PRIMARY_BUTTON_SX}>
                    Choose Spreadsheet
                    <input ref={inputRef} hidden type="file" accept=".csv,.xlsx,.xls" onChange={chooseFile} />
                  </Button>
                  {file ? <Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>{file.name}</Typography> : null}
                  <Button startIcon={<PreviewIcon />} onClick={previewFile} disabled={!file || loading} sx={PRIMARY_BUTTON_SX}>
                    Preview Rows
                  </Button>
                  <FormControlLabel
                    control={<Checkbox checked={options.merge_updates} onChange={(event) => setOptions((prev) => ({ ...prev, merge_updates: event.target.checked }))} />}
                    label="Merge/update existing records"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={options.create_missing_members} onChange={(event) => setOptions((prev) => ({ ...prev, create_missing_members: event.target.checked }))} />}
                    label="Create missing members when supported"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={options.create_missing_contacts} onChange={(event) => setOptions((prev) => ({ ...prev, create_missing_contacts: event.target.checked }))} />}
                    label="Create missing contacts when supported"
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Stack spacing={2}>
              {loading ? <BrandSpinner compact label="Building preview..." py={4} /> : null}
              {applying ? <LinearProgress /> : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
              {result ? (
                <Alert severity={result.errors?.length ? "warning" : "success"}>
                  {result.message} Created: {result.created} Updated: {result.updated} Skipped: {result.skipped}
                </Alert>
              ) : null}

              {preview ? (
                <Card sx={CARD_SX}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={`Rows ${preview.summary.total}`} />
                        <Chip label={`Valid ${preview.summary.valid}`} sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 800 }} />
                        <Chip label={`Errors ${preview.summary.errors}`} sx={{ bgcolor: "#FCE4EC", color: "#AD1457", fontWeight: 800 }} />
                        <Chip label={`Warnings ${preview.summary.warnings}`} sx={{ bgcolor: "#FFF8E1", color: "#E65100", fontWeight: 800 }} />
                      </Stack>
                      <Button startIcon={<PlayArrowIcon />} onClick={applyImport} disabled={!canApply || applying} sx={PRIMARY_BUTTON_SX}>
                        Apply Import
                      </Button>
                    </Box>
                    {!canApply ? <Alert severity="warning" sx={{ mb: 2 }}>Fix or remove rows with errors before applying. Nothing is written during preview.</Alert> : null}
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table size="small" sx={{ minWidth: 1100 }}>
                        <TableHead>
                          <TableRow>
                            {["Row", "Status", "Action", "Matches", "Errors", "Warnings", "Data"].map((label) => (
                              <TableCell key={label} sx={HEAD_CELL_SX}>{label}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row) => (
                            <TableRow key={row.row_number}>
                              <TableCell sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{row.row_number}</TableCell>
                              <TableCell><StatusChip row={row} /></TableCell>
                              <TableCell sx={{ color: BRAND_BLUE, textTransform: "capitalize" }}>{display(row.action)}</TableCell>
                              <TableCell sx={{ color: BRAND_BLUE }}>{display(row.matches)}</TableCell>
                              <TableCell sx={{ color: "#AD1457" }}>{(row.errors || []).join("; ") || "-"}</TableCell>
                              <TableCell sx={{ color: "#B7791F" }}>{(row.warnings || []).join("; ") || "-"}</TableCell>
                              <TableCell sx={{ color: "#64748b", maxWidth: 520 }}>{display(row.data)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              ) : (
                <Card sx={CARD_SX}>
                  <CardContent sx={{ p: 5, textAlign: "center" }}>
                    <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 20 }}>No import preview yet</Typography>
                    <Typography sx={{ color: "#64748b", mt: 1 }}>Choose an import type, upload a spreadsheet, and preview rows before applying.</Typography>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
